import { smartApi, type SmartInstanceStatus } from '../../api/client';
import { plural, shortWhen } from '../../lib/format';
import { useData } from '../../lib/useData';
import { Card, Chip, LoadError, Loading } from '../ui';

/**
 * Stitch Admin → System: the smart messaging service (its own container): whether it's running,
 * its last heartbeat, the event backlog, and what its timers last did (counts only).
 */

const TIMER: Record<string, string> = { retention: 'Retention', archive: 'Quiet channels', close: 'Quiet texts', trash: 'Storage clean-up', sweep: 'Tidying' };

function summary(r: Record<string, number | string | boolean | null> | null): string {
  if (!r) return '';
  const parts: string[] = [];
  const n = (k: string) => (typeof r[k] === 'number' ? (r[k] as number) : 0);
  if (n('chat')) parts.push(`${plural(n('chat'), 'message')} deleted`);
  if (n('texts')) parts.push(`${plural(n('texts'), 'text')} deleted`);
  if (n('warned')) parts.push(`${plural(n('warned'), 'heads-up', 'heads-ups')}`);
  if (n('archived')) parts.push(`${plural(n('archived'), 'channel')} archived`);
  if (n('closed')) parts.push(`${plural(n('closed'), 'conversation')} closed`);
  if (n('removed')) parts.push(`${plural(n('removed'), 'picture')} removed`);
  if (n('failed')) parts.push(`${n('failed')} failed`);
  if (typeof r.companies === 'number') parts.push(plural(r.companies, 'company', 'companies'));
  return parts.join(' · ');
}

export function SmartServiceCard() {
  const q = useData(() => smartApi.service(), []);
  if (q.error)
    return (
      <Card title="Smart messaging">
        <LoadError error={q.error} retry={q.reload} />
      </Card>
    );
  if (!q.data)
    return (
      <Card title="Smart messaging">
        <Loading />
      </Card>
    );
  const s = q.data;
  const live = s.instances.filter((i) => i.running);
  const status: SmartInstanceStatus = live[0]?.status ?? s.instances[0]?.status ?? {};
  const events = status.events ?? {};
  // Each container knows only the timers it ran: the latest run of each, whichever container.
  const timers = new Map<string, NonNullable<SmartInstanceStatus['timers']>[number]>();
  for (const i of live.length ? live : s.instances.slice(0, 1)) {
    for (const t of i.status.timers ?? []) {
      const had = timers.get(t.name);
      if (!had || t.running || (t.lastFinishedAt ?? '') > (had.lastFinishedAt ?? '')) timers.set(t.name, t);
    }
  }
  const failing = live.reduce((n, i) => n + (i.status.events?.failed ?? 0), 0);
  return (
    <Card
      title="Smart messaging"
      foot={
        <span className="muted" style={{ fontSize: 13 }}>
          Its own container (apps/smart/Dockerfile) with DATABASE_URL and REDIS_URL. See docs/smart-messaging.md.
        </span>
      }
    >
      <div className="card-body">
        <dl className="dl">
          <dt>Service</dt>
          <dd>{s.running ? <Chip tone="ok">Running{live.length > 1 ? ` (${live.length} containers)` : ''}</Chip> : <Chip tone="warn">Not running</Chip>}</dd>
          <dt>Last heartbeat</dt>
          <dd>{s.heartbeatAt ? shortWhen(s.heartbeatAt) : <span className="muted">Never</span>}</dd>
          <dt>Event backlog</dt>
          <dd>
            {s.backlog ? (
              <>
                {s.backlog.lag === null ? 'Unknown' : `${s.backlog.lag.toLocaleString('en-US')} waiting`}
                {` · ${(s.backlog.pending ?? 0).toLocaleString('en-US')} being handled`}
              </>
            ) : (
              <span className="muted">Unknown</span>
            )}
            {events.handled !== undefined && <small className="muted smart-sub">{(events.handled ?? 0).toLocaleString('en-US')} handled since it started{failing ? `, ${failing} to try again` : ''}{events.dropped ? `, ${events.dropped} dropped` : ''}</small>}
          </dd>
          {timers.size > 0 && (
            <>
              <dt>Timers</dt>
              <dd>
                <ul className="plain-list smart-timers">
                  {[...timers.values()].map((t) => (
                    <li key={t.name}>
                      <strong>{TIMER[t.name] ?? t.name}</strong>{' '}
                      <span className="muted">
                        {t.running ? 'running now' : t.lastFinishedAt ? `last ran ${shortWhen(t.lastFinishedAt)}` : 'not run here yet'}
                        {t.lastError ? ` · failed (${t.lastError})` : summary(t.lastResult) ? ` · ${summary(t.lastResult)}` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </dd>
            </>
          )}
          {typeof status.trash === 'number' && status.trash > 0 && (
            <>
              <dt>Pictures to remove</dt>
              <dd>
                {plural(status.trash, 'picture')} from deleted texts{status.storage ? '' : ': set the B2_* settings on the smart service to remove them from Stitch Block Storage'}
              </dd>
            </>
          )}
        </dl>
      </div>
    </Card>
  );
}
